/**
 * Movie Studio. Story, the reel picture, and the video timeline.
 * The design editor and Photo Studio are not on this screen.
 */

import { useEffect, useReducer, useRef, useState } from 'react';
import type { Project, ProjectPage, VideoClipRef } from '@canvas/shared-types';
import { applyEditorTemplate } from '@/data/editorTemplates';
import { MOVIE_STUDIO_NAME } from '@/data/movieProduct';
import { useAutoSave } from '@/hooks/useAutoSave';
import { requestResizeArtboard, RESIZE_ARTBOARD_EVENT } from '@/lib/artboardSize';
import { registerEditorCommand } from '@/lib/editorCommands';
import { registerEditorSaveHandler, requestEditorSave } from '@/lib/editorSave';
import { ensureMovieTimeline, syncProjectIntoStores } from '@/lib/editorSession';
import { reorderPage, resolveProjectForEditor } from '@/lib/projectPages';
import { applyTreatmentPages } from '@/lib/reel/applyReelPages';
import {
  applyStoryBeats,
  insertClip,
  isClipProject,
  nextClipId,
  patchClip,
  trackForKind,
  withCanvas,
} from '@/lib/video/timelineEdit';
import { placeGradient, toneWav } from '@/lib/video/stockLibrary';
import {
  contentPanelReducer,
  initialContentPanelState,
} from '@/lib/reel/contentPanelState';
import { HOME_OFF, TEXT_UNAVAILABLE } from '@/lib/reel/reelCopy';
import { captionMotionOf, moveReelCaption, reelCaptionPlace, withCaptionMotion, withReelCaption, type ReelCaptionMotion } from '@/lib/reel/reelCaption';
import { pageWithReelImage } from '@/lib/reel/reelImage';
import { localReelImages, renderReelFrame } from '@/lib/reel/reelImageStage';
import { pageWithReelClip, patchReelClip, reelStageSeeksClip } from '@/lib/reel/reelClip';
import {
  addReelScene,
  deleteReelScene,
  duplicateReelScene,
  renameReelScene,
  splitReelScene,
} from '@/lib/reel/reelScenes';
import type { ReelTreatment } from '@/lib/reel/reelTreatment';
import { configuredTextProviders, createTextCompleter } from '@/lib/reel/textProviders';
import { runWritersRoom, statusWhileWriting } from '@/lib/reel/writersRoom';
import { uploadProjectImage } from '@/lib/uploadAsset';
import { clampSceneMs, cueAt } from '@/lib/video/sceneTimeline';
import { createSceneRenderer, type SceneRenderer } from '@/lib/video/sceneCompositor';
import { libraryById } from '@/lib/video/libraryAssets';
import { audioTimelineClip, captionTimelineClip, imageTimelineClip, libraryAudioClip, libraryImageClip, libraryVideoClip, textClipFromLook, textTimelineClip, videoTimelineClip } from '@/lib/video/placeMedia';
import { uploadProjectVideo } from '@/lib/video/videoAssetUpload';
import { clipPlayMs, clipRefsInCanvasData } from '@/lib/video/videoClip';
import { assertSceneBudget, estimateProjectDocBytes } from '@/lib/video/videoDocLimits';
import { OPEN_VIDEO_EXPORT_EVENT } from '@/lib/video/videoEvents';
import { useCanvasStore } from '@/stores/canvasStore';
import { useProjectStore } from '@/stores/projectStore';
import { useVideoStore } from '@/stores/videoStore';
import SaveIndicator from '@/components/SaveIndicator';
import VideoExportDialog from '@/components/video/VideoExportDialog';
import VideoTimelinePanel from '@/components/video/VideoTimelinePanel';
import ProgramMonitor from './ProgramMonitor';
import ReelCaptionDrag from './ReelCaptionDrag';
import { LeftBrowser, RightBrowser, ShareDialog, SizeMenu, type LeftRail, type RightRail } from './StudioPanels';
import '@/styles/editor-adobe.css';
import '@/styles/video-editor.css';

/** Shrink a photo before it is stored, so a phone picture fits the project. */
async function prepareReelPhoto(file: File): Promise<{ file: File; width: number; height: number }> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 1280 / Math.max(bitmap.width, bitmap.height, 1));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    throw new Error('Could not read that photo.');
  }
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.82));
  if (!blob) throw new Error('Could not read that photo.');
  return { file: new File([blob], 'photo.jpg', { type: 'image/jpeg' }), width, height };
}

function fileToDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(new Error('Could not read that photo.'));
    reader.readAsDataURL(file);
  });
}

interface ReelStudioProps {
  project: Project;
  onBack: () => void;
}

/** The open movie: story on the left, picture in the center, timeline below. */
export default function ReelStudio({ project, onBack }: ReelStudioProps): JSX.Element {
  const syncedId = useRef<string | null>(null);
  if (syncedId.current !== project.projectId) {
    syncedId.current = project.projectId;
    syncProjectIntoStores(project);
    ensureMovieTimeline();
  }

  const current = useProjectStore((s) => s.currentProject) || project;
  const resolved = resolveProjectForEditor(current);
  const pages = resolved.pages;
  const activePageId = resolved.activePageId;
  const livePageId = useVideoStore((s) => cueAt(s.cues, s.currentTimeMs)?.pageId || '');
  const framePageId = livePageId || activePageId;
  const framePage = pages.find((page) => page.pageId === framePageId) || pages[0];
  const playback = useVideoStore((s) => s.playback);
  const captionPlace = framePage ? reelCaptionPlace(framePage) : null;
  const captionInPicture =
    playback !== 'stopped' &&
    (clipRefsInCanvasData(framePage?.canvasData).length > 0 ||
      (framePage ? captionMotionOf(framePage) !== 'none' : false));

  const [contentState, dispatchContent] = useReducer(
    contentPanelReducer,
    undefined,
    initialContentPanelState
  );
  const [poster, setPoster] = useState<string | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importNote, setImportNote] = useState<string | null>(null);
  const [leftRail, setLeftRail] = useState<LeftRail>('templates');
  const [rightRail, setRightRail] = useState<RightRail>('transitions');
  const [shareOpen, setShareOpen] = useState(false);
  const clipMode = useVideoStore((s) => isClipProject(s.video));
  const writeGen = useRef(0);
  const photoInput = useRef<HTMLInputElement>(null);
  const { isSaving, lastSaved, saveError, manualSave } = useAutoSave({ interval: 10000 });

  useEffect(() => {
    registerEditorSaveHandler(() => manualSave());
    return () => registerEditorSaveHandler(null);
  }, [manualSave]);

  useEffect(() => {
    if (useVideoStore.getState().video?.v === 2) return;
    const page = framePage;
    if (!page) return;
    let cancel = false;
    let renderer: SceneRenderer | null = null;
    let opening = false;
    let drawing = false;
    let pending = false;
    let lastDraw = 0;
    let mode: 'still' | 'live' = 'still';
    let generation = 0;
    let noted = false;

    const localMs = (): number => {
      const cue = useVideoStore.getState().cues.find((item) => item.pageId === page.pageId);
      return cue ? Math.max(0, useVideoStore.getState().currentTimeMs - cue.startMs) : 0;
    };
    const wantsLive = (): boolean => {
      const playback = useVideoStore.getState().playback;
      const hasClip = clipRefsInCanvasData(page.canvasData).length > 0;
      if (hasClip) return reelStageSeeksClip(playback, localMs());
      return playback !== 'stopped' && captionMotionOf(page) !== 'none';
    };
    let view = page;
    const drawStill = (): void => {
      const token = generation;
      void renderReelFrame(view.canvasData)
        .then((url) => {
          if (!cancel && token === generation && mode === 'still') setPoster(url);
        })
        .catch(() => {
          if (!cancel && token === generation && mode === 'still') setPoster(null);
        });
    };
    const failLive = (): void => {
      mode = 'still';
      if (!drawing) {
        renderer?.dispose();
        renderer = null;
      }
      if (noted) return;
      noted = true;
      const hasClip = clipRefsInCanvasData(page.canvasData).length > 0;
      setImportNote(
        hasClip
          ? 'The clip will show in the exported video.'
          : 'Title motion will show in the exported video.'
      );
      drawStill();
    };
    const drawLive = async (): Promise<void> => {
      if (cancel || mode !== 'live') return;
      if (!renderer) return;
      if (drawing) {
        pending = true;
        return;
      }
      const now = performance.now();
      if (now - lastDraw < 80) return;
      lastDraw = now;
      drawing = true;
      try {
        const el = await renderer.renderAt(localMs());
        if (!cancel && mode === 'live') setPoster(el.toDataURL('image/jpeg', 0.72));
      } catch {
        if (!cancel && mode === 'live') failLive();
      } finally {
        drawing = false;
        if (mode !== 'live') {
          renderer?.dispose();
          renderer = null;
          pending = false;
          return;
        }
        if (pending && !cancel) {
          pending = false;
          void drawLive();
        }
      }
    };
    const openLive = (): void => {
      if (renderer || opening || cancel || noted) return;
      const scene = useVideoStore.getState().video?.scenes.find((item) => item.pageId === page.pageId);
      if (!scene) return;
      opening = true;
      generation += 1;
      void createSceneRenderer({
        page: view,
        scene,
        out: {
          width: Math.max(1, Math.round((page.canvasData.width || 1080) * 0.45)),
          height: Math.max(1, Math.round((page.canvasData.height || 1920) * 0.45)),
        },
      })
        .then((created) => {
          opening = false;
          if (cancel || mode !== 'live') {
            created.dispose();
            return;
          }
          renderer = created;
          lastDraw = 0;
          void drawLive();
        })
        .catch(() => {
          opening = false;
          if (!cancel) failLive();
        });
    };
    const sync = (): void => {
      if (cancel) return;
      if (wantsLive()) {
        mode = 'live';
        if (renderer) void drawLive();
        else openLive();
        return;
      }
      if (mode === 'still') return;
      mode = 'still';
      generation += 1;
      if (!drawing) {
        renderer?.dispose();
        renderer = null;
      }
      drawStill();
    };

    let revoke = (): void => {};
    let ready = false;
    const unsub = useVideoStore.subscribe(() => {
      if (ready) sync();
    });
    void localReelImages(page.canvasData)
      .then((local) => {
        if (cancel) {
          local.revoke();
          return;
        }
        revoke = local.revoke;
        view = { ...page, canvasData: local.data };
        if (local.missed) setImportNote('The photo is saved, but this browser could not draw it.');
        ready = true;
        drawStill();
        sync();
      })
      .catch(() => {
        if (cancel) return;
        ready = true;
        drawStill();
        sync();
      });
    return () => {
      cancel = true;
      unsub();
      renderer?.dispose();
      revoke();
    };
  }, [framePage]);

  const commit = (nextPages: ProjectPage[], nextActiveId: string): void => {
    const projectNow = useProjectStore.getState().currentProject;
    if (!projectNow) return;
    const active = nextPages.find((page) => page.pageId === nextActiveId) || nextPages[0];
    if (!active) return;
    useProjectStore.getState().updateProject({
      ...projectNow,
      pages: nextPages,
      activePageId: active.pageId,
      canvasData: active.canvasData,
    });
    useCanvasStore.getState().setCanvasData(active.canvasData);
    useCanvasStore.getState().markDirty(true);
    useVideoStore.getState().syncToPages(nextPages);
  };

  useEffect(() => {
    const doc = useProjectStore.getState().currentProject;
    if (!doc || doc.projectId !== project.projectId) return;
    const view = resolveProjectForEditor(doc);
    view.pages.forEach((page, index) => {
      if (/^Scene \d+$/.test(page.name.trim())) return;
      dispatchContent({ type: 'setLine', index: index + 1, line: page.name });
    });
    let changed = false;
    const next = view.pages.map((page) => {
      const updated = withReelCaption(page, page.name);
      if (updated !== page) changed = true;
      return updated;
    });
    if (changed) commit(next, view.activePageId);
  }, [project.projectId]);

  const allowAnotherScene = (): boolean => {
    const video = useVideoStore.getState().video;
    const doc = useProjectStore.getState().currentProject;
    if (!video || !doc) return false;
    const budget = assertSceneBudget(video, estimateProjectDocBytes(doc));
    if (!budget.ok) {
      useVideoStore.getState().setNotice(budget.reason || 'Cannot add another scene.');
      return false;
    }
    return true;
  };

  useEffect(() => {
    const add = () => {
      if (!allowAnotherScene()) return;
      const doc = useProjectStore.getState().currentProject;
      const list = doc ? resolveProjectForEditor(doc).pages : [];
      const next = addReelScene(list, doc?.width || 1080, doc?.height || 1920);
      commit(next, next[next.length - 1]?.pageId || '');
    };
    const duplicate = () => {
      if (!allowAnotherScene()) return;
      const doc = useProjectStore.getState().currentProject;
      if (!doc) return;
      const view = resolveProjectForEditor(doc);
      const copied = duplicateReelScene(view.pages, view.activePageId);
      if (copied) {
        commit(
          copied.pages.map((page) => withReelCaption(page, page.name)),
          copied.activePageId
        );
      }
    };
    const remove = () => {
      const doc = useProjectStore.getState().currentProject;
      if (!doc) return;
      const view = resolveProjectForEditor(doc);
      const removed = deleteReelScene(view.pages, view.activePageId);
      if (removed) commit(removed.pages, removed.activePageId);
    };
    const openExport = () => setExportOpen(true);
    registerEditorCommand('scene-add', add);
    registerEditorCommand('scene-duplicate', duplicate);
    registerEditorCommand('scene-delete', remove);
    registerEditorCommand('video-export', openExport);
    const onExportEvent = () => setExportOpen(true);
    window.addEventListener(OPEN_VIDEO_EXPORT_EVENT, onExportEvent);
    return () => {
      registerEditorCommand('scene-add', null);
      registerEditorCommand('scene-duplicate', null);
      registerEditorCommand('scene-delete', null);
      registerEditorCommand('video-export', null);
      window.removeEventListener(OPEN_VIDEO_EXPORT_EVENT, onExportEvent);
    };
  });

  useEffect(() => {
    const onResize = (ev: Event) => {
      const detail = (ev as CustomEvent<{ width: number; height: number }>).detail;
      const doc = useProjectStore.getState().currentProject;
      if (!doc || !detail?.width || !detail?.height) return;
      const width = Math.max(32, Math.round(detail.width));
      const height = Math.max(32, Math.round(detail.height));
      const view = resolveProjectForEditor(doc);
      const nextPages = view.pages.map((page) => ({
        ...page,
        canvasData: { ...page.canvasData, width, height },
      }));
      useProjectStore.getState().updateProject({
        ...doc,
        width,
        height,
        pages: nextPages,
        activePageId: view.activePageId,
        canvasData: nextPages.find((page) => page.pageId === view.activePageId)?.canvasData || doc.canvasData,
      });
      useCanvasStore.getState().markDirty(true);
    };
    window.addEventListener(RESIZE_ARTBOARD_EVENT, onResize);
    return () => window.removeEventListener(RESIZE_ARTBOARD_EVENT, onResize);
  }, []);

  const applyTreatment = (treatment: ReelTreatment): void => {
    const doc = useProjectStore.getState().currentProject;
    if (!doc) return;
    const view = resolveProjectForEditor(doc);
    const next = applyTreatmentPages(view.pages, treatment.beats, {
      width: doc.width || 1080,
      height: doc.height || 1920,
    }, { embedCaption: false });
    commit(next, next[0]?.pageId || view.activePageId);
    useVideoStore.getState().changeTimeline((video) =>
      applyStoryBeats(
        video,
        next.map((page) => ({ pageId: page.pageId, name: page.name }))
      )
    );
  };

  const onWrite = (): void => {
    const idea = contentState.idea.trim();
    if (!idea) return;
    const providers = configuredTextProviders();
    if (providers.length === 0) {
      dispatchContent({ type: 'markWriting' });
      dispatchContent({ type: 'markFailed', message: TEXT_UNAVAILABLE });
      return;
    }
    const gen = writeGen.current + 1;
    writeGen.current = gen;
    dispatchContent({ type: 'markWriting' });
    void runWritersRoom(idea, {
      providers,
      complete: createTextCompleter(),
      onProvider: (provider) => {
        if (writeGen.current !== gen) return;
        dispatchContent({ type: 'setStatus', status: statusWhileWriting(provider) });
      },
    }).then((result) => {
      if (writeGen.current !== gen) return;
      if (!result.ok || !result.treatment) {
        dispatchContent({
          type: 'markFailed',
          message: result.error || TEXT_UNAVAILABLE,
        });
        return;
      }
      dispatchContent({ type: 'markWritten', treatment: result.treatment });
      applyTreatment(result.treatment);
    });
  };

  const onLine = (index: number, line: string): void => {
    dispatchContent({ type: 'setLine', index, line });
    const page = pages[index - 1];
    if (!page) return;
    const name = line.trim() || `Scene ${index}`;
    const renamed = renameReelScene(pages, page.pageId, name).map((item) =>
      item.pageId === page.pageId ? withReelCaption(item, name) : item
    );
    commit(renamed, activePageId);
    const videoNow = useVideoStore.getState().video;
    if (isClipProject(videoNow)) {
      const captions = videoNow.clips.filter((clip) => clip.groupId === 'story' && clip.kind === 'caption');
      const caption = captions[index - 1];
      if (caption?.caption) {
        useVideoStore.getState().changeTimeline((video) =>
          patchClip(video, caption.id, { name, caption: { ...caption.caption!, text: name } })
        );
      }
    }
  };

  const onMoveCaption = (x: number, y: number): void => {
    if (!framePage) return;
    const doc = useProjectStore.getState().currentProject;
    if (!doc) return;
    const width = framePage.canvasData.width || 1080;
    const height = framePage.canvasData.height || 1920;
    const view = resolveProjectForEditor(doc);
    const next = view.pages.map((item) =>
      item.pageId === framePage.pageId ? moveReelCaption(item, x * width, y * height) : item
    );
    commit(next, view.activePageId);
  };

  const fitSceneToClip = (pageId: string, list: ProjectPage[]): void => {
    const page = list.find((item) => item.pageId === pageId);
    const placed = page ? clipRefsInCanvasData(page.canvasData)[0] : undefined;
    if (!placed) return;
    useVideoStore.getState().setSceneDuration(pageId, clampSceneMs(clipPlayMs(placed)));
  };

  const onImportVideo = (pageId: string, file: File): void => {
    const projectId = useProjectStore.getState().currentProject?.projectId || '';
    setImportBusy(true);
    setImportNote(null);
    void uploadProjectVideo({ file, projectId })
      .then((imported) => {
        const doc = useProjectStore.getState().currentProject;
        if (!doc) return;
        const view = resolveProjectForEditor(doc);
        const currentPage = view.pages.find((item) => item.pageId === pageId);
        const previous = currentPage ? clipRefsInCanvasData(currentPage.canvasData)[0] : undefined;
        const clip = previous
          ? {
              ...imported.clip,
              trimStartMs: previous.trimStartMs,
              trimEndMs: previous.trimEndMs,
              rate: previous.rate,
              muted: previous.muted,
              volume: previous.volume,
              loop: previous.loop,
            }
          : imported.clip;
        const next = view.pages.map((item) =>
          item.pageId === pageId ? pageWithReelClip(item, clip) : item
        );
        commit(next, pageId);
        fitSceneToClip(pageId, next);
        setImportNote(imported.backedUp ? 'Video imported.' : 'Stored in this browser.');
      })
      .catch((err: unknown) => {
        setImportNote(err instanceof Error ? err.message : 'Import failed.');
      })
      .finally(() => setImportBusy(false));
  };

  const onPatchClip = (pageId: string, patch: Partial<VideoClipRef>): void => {
    const doc = useProjectStore.getState().currentProject;
    if (!doc) return;
    const view = resolveProjectForEditor(doc);
    const next = view.pages.map((item) =>
      item.pageId === pageId ? patchReelClip(item, patch) : item
    );
    commit(next, view.activePageId);
    if (patch.rate !== undefined || patch.trimStartMs !== undefined || patch.trimEndMs !== undefined) {
      fitSceneToClip(pageId, next);
    }
  };

  const onCaptionMotion = (pageId: string, motion: ReelCaptionMotion): void => {
    const doc = useProjectStore.getState().currentProject;
    if (!doc) return;
    const view = resolveProjectForEditor(doc);
    const page = view.pages.find((item) => item.pageId === pageId);
    if (!page) return;
    const updated = withCaptionMotion(page, motion);
    if (updated === page) {
      if (captionMotionOf(page) !== motion) setImportNote('Type a line, then choose a title motion.');
      return;
    }
    commit(
      view.pages.map((item) => (item.pageId === pageId ? updated : item)),
      view.activePageId
    );
    setImportNote(null);
  };

  const onInsertImage = (pageId: string, file: File): void => {
    const projectId = useProjectStore.getState().currentProject?.projectId || '';
    setImportBusy(true);
    setImportNote(null);
    void prepareReelPhoto(file)
      .then((prepared) =>
        Promise.all([
          fileToDataUrl(prepared.file),
          uploadProjectImage({ file: prepared.file, projectId }),
        ]).then(([dataUrl, uploaded]) => ({ prepared, uploaded, dataUrl }))
      )
      .then(({ prepared, uploaded, dataUrl }) => {
        const doc = useProjectStore.getState().currentProject;
        if (!doc) return;
        const view = resolveProjectForEditor(doc);
        const next = view.pages.map((item) =>
          item.pageId === pageId
            ? pageWithReelImage(item, {
                src: dataUrl || uploaded.url,
                width: prepared.width,
                height: prepared.height,
                storagePath: uploaded.path,
              })
            : item
        );
        commit(next, pageId);
        setImportNote('Photo added.');
      })
      .catch((err: unknown) => {
        setImportNote(err instanceof Error ? err.message : 'Could not add that photo.');
      })
      .finally(() => setImportBusy(false));
  };

  const onSplit = (): void => {
    const doc = useProjectStore.getState().currentProject;
    const video = useVideoStore.getState().video;
    if (!doc || !video) return;
    const view = resolveProjectForEditor(doc);
    const cue = useVideoStore.getState().cues.find((item) => item.pageId === view.activePageId);
    const scene = video.scenes.find((item) => item.pageId === view.activePageId);
    if (!cue || !scene) return;
    const local = useVideoStore.getState().currentTimeMs - cue.startMs;
    const split = splitReelScene(view.pages, scene.pageId, local, scene.durationMs);
    if (!split) {
      setImportNote('Move the playhead into the middle of this scene, then split.');
      return;
    }
    if (!allowAnotherScene()) return;
    commit(split.pages, split.rightId);
    useVideoStore.getState().setSceneDuration(split.leftId, split.leftMs);
    useVideoStore.getState().setSceneDuration(split.rightId, split.rightMs);
    setImportNote(null);
  };

  const placeAt = (clip: Awaited<ReturnType<typeof textTimelineClip>>, trackId?: string): void => {
    const video = useVideoStore.getState().video;
    const kind = clip.kind === 'audio' ? 'audio' : clip.kind === 'text' ? 'text' : clip.kind === 'caption' ? 'caption' : 'visual';
    const placed = video ? { ...clip, trackId: trackForKind(video, trackId ?? clip.trackId, kind) } : clip;
    useVideoStore.getState().changeTimeline((doc) => insertClip(doc, placed));
    useVideoStore.getState().selectClip(placed.id);
  };

  const placeLibrary = (id: string, atMs = useVideoStore.getState().currentTimeMs, trackId?: string): void => {
    const item = libraryById(id);
    if (!item) return;
    setImportBusy(true);
    setImportNote(null);
    const job =
      item.section === 'audio'
        ? libraryAudioClip(item, atMs)
        : item.section === 'video'
          ? libraryVideoClip(item, atMs)
          : libraryImageClip(item, atMs);
    void job
      .then((clip) => {
        placeAt(clip, trackId);
        setImportNote(null);
      })
      .catch((err: unknown) => {
        setImportNote(err instanceof Error ? err.message : 'Could not add that library item.');
      })
      .finally(() => setImportBusy(false));
  };

  const importFile = (file: File, atMs = useVideoStore.getState().currentTimeMs, durationMs?: number, trackId?: string): Promise<void> => {
    const projectId = useProjectStore.getState().currentProject?.projectId || '';
    setImportBusy(true);
    setImportNote(null);
    const job = file.type.startsWith('audio/')
      ? audioTimelineClip(file, projectId, atMs, undefined, durationMs)
      : file.type.startsWith('image/')
        ? imageTimelineClip(file, projectId, atMs)
        : videoTimelineClip(file, projectId, atMs, durationMs);
    return job
      .then((clip) => {
        placeAt(clip, trackId);
        setImportNote(null);
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'Could not import that file.';
        setImportNote(message);
        throw new Error(message);
      })
      .finally(() => setImportBusy(false));
  };

  const addTemplate = (id: string, atMs = useVideoStore.getState().currentTimeMs): void => {
    const before = useVideoStore.getState().video?.clips?.length ?? 0;
    useVideoStore.getState().changeTimeline((video) => applyEditorTemplate(video, id, atMs));
    const next = useVideoStore.getState().video;
    const doc = useProjectStore.getState().currentProject;
    if (before === 0 && doc && next?.canvas && (doc.width !== next.canvas.width || doc.height !== next.canvas.height)) {
      useProjectStore.getState().updateProject({
        ...doc,
        width: next.canvas.width,
        height: next.canvas.height,
      });
      requestResizeArtboard(next.canvas.width, next.canvas.height);
    }
  };

  useEffect(() => {
    const onDrop = (event: Event): void => {
      const detail = (event as CustomEvent<{ action: string; id?: string; text?: string; trackId?: string; atMs: number }>).detail;
      if (!detail) return;
      const atMs = detail.atMs || 0;
      if (detail.action === 'template' && detail.id) addTemplate(detail.id, atMs);
      if (detail.action === 'gradient' && detail.id) {
        useVideoStore.getState().changeTimeline((video) => placeGradient(video, detail.id || '', atMs));
      }
      if (detail.action === 'tone') {
        importFile(new File([toneWav()], 'soft-bed.wav', { type: 'audio/wav' }), atMs, undefined, detail.trackId);
      }
      if (detail.action === 'library' && detail.id) placeLibrary(detail.id, atMs, detail.trackId);
      if (detail.action === 'text') {
        placeAt(textClipFromLook(detail.id || 'plain', atMs, detail.text), detail.trackId);
      }
      if (detail.action === 'duplicate' && detail.id) {
        const video = useVideoStore.getState().video;
        const source = video?.clips?.find((clip) => clip.id === detail.id);
        if (!source) return;
        placeAt({
          ...source,
          id: nextClipId('copy'),
          startMs: atMs,
          trackId: detail.trackId || source.trackId,
          groupId: undefined,
          name: `${source.name} copy`,
        });
      }
    };
    const onLogo = (): void => {
      const video = useVideoStore.getState().video;
      const url = video?.brand?.logoUrl;
      if (!url) return;
      placeAt({
        id: nextClipId('logo'),
        trackId: 'visual-a',
        startMs: useVideoStore.getState().currentTimeMs,
        durationMs: 4000,
        name: 'Logo',
        kind: 'image',
        image: {
          v: 1,
          source: 'remote',
          url,
          mimeType: 'image/png',
          width: 256,
          height: 256,
          fit: 'contain',
        },
      });
    };
    window.addEventListener('craftara:drop-clip', onDrop);
    window.addEventListener('craftara:add-logo', onLogo);
    return () => {
      window.removeEventListener('craftara:drop-clip', onDrop);
      window.removeEventListener('craftara:add-logo', onLogo);
    };
  });

  const close = (): void => {
    if (leaving) return;
    setLeaving(true);
    void requestEditorSave().finally(() => {
      onBack();
      setLeaving(false);
    });
  };

  const panelProps = {
    contentState,
    onIdea: (idea: string) => dispatchContent({ type: 'setIdea', idea }),
    onLine,
    onWrite,
    onMakePictures: () => dispatchContent({ type: 'markFailed', message: HOME_OFF }),
    onUsePhotos: () => photoInput.current?.click(),
    onToggleAdjust: () => dispatchContent({ type: 'toggleAdjust' }),
    onMovePictures: (movePictures: boolean) => dispatchContent({ type: 'setMovePictures', movePictures }),
    onTemplate: (id: string) => addTemplate(id),
    onGradient: (id: string) => {
      useVideoStore.getState().changeTimeline((video) => placeGradient(video, id, useVideoStore.getState().currentTimeMs));
    },
    onTone: () => importFile(new File([toneWav()], 'soft-bed.wav', { type: 'audio/wav' })),
    onLibrary: (id: string) => placeLibrary(id),
    onText: (lookId: string, sample?: string) => {
      placeAt(textClipFromLook(lookId, useVideoStore.getState().currentTimeMs, sample));
    },
    onCaption: (text: string, source: 'manual' | 'auto', atMs?: number) => {
      placeAt(captionTimelineClip(text, atMs ?? useVideoStore.getState().currentTimeMs, source));
    },
    onImport: (file: File) => importFile(file),
    onRecorded: (file: File, _kind: 'video' | 'audio', atMs: number, durationMs?: number) => importFile(file, atMs, durationMs),
    busy: importBusy,
    note: importNote,
  };

  return (
    <div className="ps-app vt-reel vt-studio">
      <header className="vt-reel-top">
        <button type="button" className="vt-btn" onClick={close} disabled={leaving}>
          Close
        </button>
        <div className="vt-reel-brand">{current.name || MOVIE_STUDIO_NAME}</div>
        <div className="vt-reel-top-actions">
          <button type="button" className="vt-btn" title="Undo" onClick={() => useVideoStore.getState().undo()}>
            Undo
          </button>
          <button type="button" className="vt-btn" title="Redo" onClick={() => useVideoStore.getState().redo()}>
            Redo
          </button>
          <SizeMenu
            onPick={(preset) => {
              const doc = useProjectStore.getState().currentProject;
              if (!doc) return;
              useProjectStore.getState().updateProject({ ...doc, width: preset.width, height: preset.height });
              useVideoStore.getState().changeTimeline((video) => {
                const sized = withCanvas(video, {
                  width: preset.width,
                  height: preset.height,
                  bg: video.canvas?.bg || '#111111',
                });
                return {
                  ...sized,
                  exportSettings: { ...sized.exportSettings, platformId: preset.id },
                };
              });
              requestResizeArtboard(preset.width, preset.height);
            }}
          />
          <button type="button" className="vt-btn" onClick={() => setShareOpen(true)}>
            Share
          </button>
          <button type="button" className="vt-btn vt-btn-primary" onClick={() => setExportOpen(true)}>
            Export
          </button>
        </div>
      </header>
      <div className="vt-reel-body">
        <nav className="vt-icon-rail" aria-label="Libraries">
          {(
            [
              ['media', 'Media'],
              ['record', 'Record'],
              ['library', 'Library'],
              ['text', 'Text'],
              ['brand', 'Brand'],
              ['templates', 'Templates'],
              ['story', 'Story'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={leftRail === id ? 'active' : ''}
              onClick={() => setLeftRail(id)}
            >
              {label}
            </button>
          ))}
        </nav>
        <aside className="vt-reel-story" aria-label="Browser">
          <LeftBrowser left={leftRail} right={rightRail} {...panelProps} />
        </aside>
        <div className="vt-reel-stage">
          {clipMode ? (
            <ProgramMonitor pages={pages} />
          ) : (
            <div className="vt-reel-frame" style={{ background: framePage?.canvasData.background || '#111' }}>
              {poster ? <img src={poster} alt="" /> : null}
              {captionPlace && !captionInPicture ? (
                <ReelCaptionDrag
                  text={captionPlace.text}
                  x={captionPlace.x}
                  y={captionPlace.y}
                  onMove={onMoveCaption}
                />
              ) : null}
            </div>
          )}
        </div>
        <nav className="vt-icon-rail vt-icon-rail-right" aria-label="Clip tools">
          <button type="button" className={rightRail === 'captions' ? 'active' : ''} onClick={() => setRightRail('captions')}>
            Captions
          </button>
          <button type="button" className={rightRail === 'transitions' ? 'active' : ''} onClick={() => setRightRail('transitions')}>
            Transition
          </button>
        </nav>
        {rightRail ? <RightBrowser right={rightRail} onCaption={panelProps.onCaption} /> : null}
      </div>
      <VideoTimelinePanel
        chrome="reel"
        pages={pages}
        activePageId={activePageId}
        onSelect={(pageId) => {
          if (pageId !== activePageId) commit(pages, pageId);
        }}
        onRename={(pageId, name) => {
          const renamed = renameReelScene(pages, pageId, name).map((item) =>
            item.pageId === pageId ? withReelCaption(item, name) : item
          );
    commit(renamed, activePageId);
    const videoNow = useVideoStore.getState().video;
    if (isClipProject(videoNow)) {
      const captions = videoNow.clips.filter((clip) => clip.groupId === 'story' && clip.kind === 'caption');
      const index = pages.findIndex((item) => item.pageId === pageId);
      const caption = captions[index];
      if (caption?.caption) {
        useVideoStore.getState().changeTimeline((video) =>
          patchClip(video, caption.id, { name, caption: { ...caption.caption!, text: name } })
        );
      }
    }
    const index = pages.findIndex((item) => item.pageId === pageId);
          if (index >= 0) dispatchContent({ type: 'setLine', index: index + 1, line: name });
        }}
        onReorder={(fromId, toId) => {
          const next = reorderPage(pages, fromId, toId);
          if (next !== pages) commit(next, activePageId);
        }}
        onImportVideo={onImportVideo}
        onPatchClip={onPatchClip}
        onSplit={onSplit}
        onCaptionMotion={onCaptionMotion}
        onInsertImage={onInsertImage}
        importBusy={importBusy}
        importNote={importNote}
      />
      <input
        ref={photoInput}
        type="file"
        accept="image/*"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) onInsertImage(activePageId, file);
        }}
      />
      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} onDownload={() => { setShareOpen(false); setExportOpen(true); }} />
      <VideoExportDialog
        open={exportOpen}
        projectId={current.projectId}
        projectName={current.name}
        getPages={() => {
          const doc = useProjectStore.getState().currentProject;
          return doc ? resolveProjectForEditor(doc).pages : pages;
        }}
        onClose={() => setExportOpen(false)}
      />
      <SaveIndicator
        isSaving={isSaving}
        lastSaved={lastSaved}
        error={saveError}
        showDuration={2000}
      />
    </div>
  );
}
